import { FileOpen, Search } from "@mui/icons-material";
import { LoadingButton } from "@mui/lab";
import {
  Button,
  Card,
  CardActions,
  CardContent,
  CardHeader,
  Stack,
  Typography,
} from "@mui/material";
import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useRecoilState, useResetRecoilState } from "recoil";
import {
  filterSearchSelectedState,
  hoursAvailableState,
  professionalsMatchCovenantState,
  professionalsNoMatchCovenantState,
} from "src/SearchHours/state/atoms";
import { ProfileService } from "src/services/patient/profile";
import theme from "src/theme";

const colorsByService = {
  URG: "#005398",
  CEM: theme.palette.secondary.main,
};

const CardClinicalHistory = ({ history }) => {
  const [filterSearchSelected, setFilterSearchSelected] = useRecoilState(
    filterSearchSelectedState,
  );
  const resetProfessionalsMatchCovenant = useResetRecoilState(
    professionalsMatchCovenantState,
  );
  const resetProfessionalsNoMatchCovenant = useResetRecoilState(
    professionalsNoMatchCovenantState,
  );
  const resetHoursAvailable = useResetRecoilState(hoursAvailableState);
  const navigate = useNavigate();

  const [loading, setLoading] = useState({
    1: false,
    2: false,
    3: false,
  });

  const searchUrl = () => {
    resetProfessionalsMatchCovenant();
    resetProfessionalsNoMatchCovenant();
    resetHoursAvailable();
    setFilterSearchSelected({
      ...filterSearchSelected,
      professionalSelected: {
        value: history.nombrePrestador,
        code: history.rutPrestador,
        showInFilter: true,
        canDeleteFilter: true,
      },
    });
    setTimeout(() => {
      navigate(`${process.env.REACT_APP_ROOT_SERVER_PATH}/resultado-busqueda`);
    }, 1000);
  };

  const isCEM = history.codigoServicio === "CEM";

  //abrir base64pdf en nueva pestaña
  const openBase64Pdf = async (params) => {
    setLoading((prev) => ({ ...prev, [params.tipo]: true }));
    const { data } = await ProfileService.getDocumentPdf(params);
    console.log(data.documento);

    const binary = atob(data.documento);
    const bytes = new Uint8Array(binary.length);
    for (let i = 0; i < binary.length; i++) {
      bytes[i] = binary.charCodeAt(i);
    }
    const blob = new Blob([bytes], { type: "application/pdf" });

    const url = URL.createObjectURL(blob);
    setLoading((prev) => ({ ...prev, [params.tipo]: false }));
    window.open(url, "_blank");
  };

  return (
    <Card variant="elevation">
      <CardHeader
        sx={{
          backgroundColor:
            colorsByService[history.codigoServicio] ||
            theme.palette.secondary.main,
          color: theme.palette.secondary.contrastText,
        }}
        title={
          <Stack
            direction="row"
            justifyContent="space-between"
            alignItems="center"
          >
            <small>{history.servicio}</small>
            <small>{history.fechaConsulta.split(" ")[0]}</small>
          </Stack>
        }
      />
      <CardContent sx={{ borderBottom: "1px solid #E5E7EB" }}>
        <Stack spacing={1}>
          <Typography variant="subtitle1" fontSize={18} lineHeight={1.25}>
            {history.nombrePrestador}
          </Typography>
          <Typography variant="subtitle2" fontSize={12} fontWeight={700}>
            {history.especialidad}
          </Typography>
          <Typography variant="caption" fontSize={12} fontWeight={700}>
            {history.motivoConsulta}
          </Typography>
        </Stack>
      </CardContent>
      <CardActions
        disableSpacing
        sx={{
          display: "flex",
          flexDirection: { xs: "column", md: "row" },
          justifyContent: "space-between",
          alignItems: { xs: "flex-start", md: "center" },
          gap: 1,
        }}
        style={
          isCEM
            ? { justifyContent: "space-between" }
            : { justifyContent: "flex-end" }
        }
      >
        {/* Botón de búsqueda */}
        {isCEM && (
          <Button
            size="small"
            sx={{
              fontSize: "0.75rem",
              fontWeight: 600,
              color: theme.palette.info.dark,
            }}
            startIcon={<Search />}
            onClick={searchUrl}
          >
            Buscar horas disponibles
          </Button>
        )}

        {/* Documentos o texto */}
        <Stack
          direction={{ xs: "column", md: "row" }}
          spacing={1}
          alignItems={{ xs: "flex-start", md: "center" }}
        >
          {history.tieneRecetas === "S" && (
            <LoadingButton
              size="small"
              loading={loading[1]}
              disabled={loading[2] || loading[3]}
              loadingPosition="start"
              sx={{
                fontSize: "0.75rem",
                fontWeight: 600,
                color: theme.palette.info.dark,
              }}
              startIcon={<FileOpen />}
              onClick={() =>
                openBase64Pdf({
                  pacNumFicha: history.numeroFicha,
                  ingCorrel: history.correlativoIngreso,
                  tipo: 1,
                  correlativo: history.farCorrel,
                })
              }
            >
              Receta
            </LoadingButton>
          )}
          {history.tieneLab === "S" && (
            <LoadingButton
              size="small"
              loading={loading[2]}
              disabled={loading[1] || loading[3]}
              loadingPosition="start"
              sx={{
                fontSize: "0.75rem",
                fontWeight: 600,
                color: theme.palette.info.dark,
              }}
              startIcon={<FileOpen />}
              onClick={() =>
                openBase64Pdf({
                  pacNumFicha: history.numeroFicha,
                  ingCorrel: history.correlativoIngreso,
                  tipo: 2,
                  correlativo: history.faeHojaImpLab,
                })
              }
            >
              Orden Laboratorio
            </LoadingButton>
          )}
          {history.tieneImg === "S" && (
            <LoadingButton
              size="small"
              loading={loading[3]}
              disabled={loading[1] || loading[2]}
              loadingPosition="start"
              sx={{
                fontSize: "0.75rem",
                fontWeight: 600,
                color: theme.palette.info.dark,
              }}
              startIcon={<FileOpen />}
              onClick={() =>
                openBase64Pdf({
                  pacNumFicha: history.numeroFicha,
                  ingCorrel: history.correlativoIngreso,
                  tipo: 3,
                  correlativo: history.faeHojaImpImg,
                })
              }
            >
              Orden Imagenología
            </LoadingButton>
          )}

          {history.tieneRecetas === "N" &&
            history.tieneLab === "N" &&
            history.tieneImg === "N" && (
              <Typography
                variant="caption"
                fontSize="0.75rem"
                pl={"4px"}
                color={theme.palette.text.secondary}
              >
                Sin documentos asociados
              </Typography>
            )}
        </Stack>
      </CardActions>
    </Card>
  );
};

export default CardClinicalHistory;
